import * as React from "react";
import { makeStyles } from "@fluentui/react-components";
import { useNavigate, useParams, Routes, Route, Navigate } from "react-router-dom";
import ProgressStripe from "./ProgressStripe";
import Slide from "./Slide";
import Button from "./Button";
import Slides from "../slides";
import { SlideProvider } from "./SlideContext";
import useWindowWidth from "../hooks/useWindowWidth";

/* global localStorage window setTimeout clearTimeout */

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    width: "100%",
    background: "#dce3fe",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "#333",
  },
  content: {
    maxWidth: "1220px",
    minHeight: "80vh",
    width: "100%",
    padding: "30px",
    borderRadius: "20px",
    margin: "50px",
    background: "#fff",
    display: "flex",
    flexDirection: "column",
  },
  filler: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "stretch",
    alignItems: "stretch",
  },
  buttons: {
    display: "flex",
    width: "100%",
    gap: "10px",
    marginTop: "20px",
  },
  mobileMessage: {
    padding: "40px",
    fontSize: "1.2rem",
    lineHeight: "1.5",
    color: "#666",
    textAlign: "center",
  },
});

const STORAGE_KEY = "tutorial_props";

const AppContent = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { slideId } = useParams();
  const windowWidth = useWindowWidth();

  const currentSlideIndex = React.useMemo(() => {
    const index = Slides.findIndex((s) => s.id === slideId);
    return index !== -1 ? index : 0;
  }, [slideId]);

  const [slideProps, setProps] = React.useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : { totalSteps: 0 };
  });

  const [isNextDisabled, setIsNextDisabled] = React.useState(false);
  const [pptFrame, setPPTFrameState] = React.useState({ width: 0, height: 0, x: 0, y: 0 });

  const currentSlide = Slides[currentSlideIndex];

  React.useEffect(() => {
    if (currentSlide && currentSlide.noNextDelay) {
      setIsNextDisabled(false);
      return;
    }

    setIsNextDisabled(true);
    const timer = setTimeout(() => {
      setIsNextDisabled(false);
    }, 4000);
    return () => clearTimeout(timer);
  }, [slideId, currentSlide]);

  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(slideProps));
  }, [slideProps]);

  const goTo = React.useCallback(
    (id) => {
      if (typeof id === "function") id = id(slideProps);

      const slide = Slides.find((s) => s.id === id);
      if (slide && slide.href) {
        window.top.location.href = slide.href;
        return;
      }

      navigate(`/${id}`);
    },
    [slideProps, navigate]
  );

  const setProp = React.useCallback((key, value) => {
    setProps((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setPPTFrame = React.useCallback((frame) => {
    setPPTFrameState(frame);
  }, []);

  const CurrentSlideComponent = currentSlide.Component;

  const progress = typeof currentSlide.step === "function" ? currentSlide.step(slideProps) : currentSlide.step;

  const slideContextValue = React.useMemo(
    () => ({ props: slideProps, goTo, setProp, pptFrame, setPPTFrame }),
    [slideProps, goTo, setProp, pptFrame, setPPTFrame]
  );

  React.useEffect(() => {
    const slide = Slides.find((s) => s.id === slideId);
    if (slide && slide.href) {
      window.top.location.href = slide.href;
    }
  }, [slideId]);

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {windowWidth < 1000 ? (
          <div className={styles.mobileMessage}>
            Please use a larger window size or a desktop device to view the tutorial.
          </div>
        ) : (
          <>
            <ProgressStripe steps={slideProps.totalSteps} progress={progress} />
            <div className={styles.filler}>
              <SlideProvider value={slideContextValue}>
                <Slide key={slideId} title={currentSlide.title} subtitle={currentSlide.subtitle}>
                  {CurrentSlideComponent ? <CurrentSlideComponent /> : currentSlide.content}
                </Slide>
              </SlideProvider>
            </div>
            {currentSlide.next && (
              <div className={styles.buttons}>
                <Button disabled={currentSlide?.step === 1} onClick={() => navigate(-1)}>
                  Previous
                </Button>
                <Button primary onClick={() => goTo(currentSlide.next)} disabled={isNextDisabled}>
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const App = () => {
  return (
    <Routes>
      <Route path="/:slideId" element={<AppContent />} />
      <Route path="/" element={<Navigate to={`/${Slides[0].id}`} replace />} />
    </Routes>
  );
};

export default App;
